import { useCallback, useMemo } from 'react'

import { fetchExternalFile, fetchExternalThumbnail, streamExternalVideoDownload } from '@/api/api'
import type { ExternalDownloadCompleted } from '@/api/type'
import type { ExternalDownloadConfig } from '@/hooks/useExternalDownload'
import { useExternalDownload } from '@/hooks/useExternalDownload'
import { db } from '@/lib/db'

export const useExternalVideoDownload = () => {
  const config = useMemo<ExternalDownloadConfig<ExternalDownloadCompleted>>(
    () => ({
      mediaLabel: '動画',
      fetchFile: fetchExternalFile,
      fetchThumbnail: fetchExternalThumbnail,
      saveToDb: async ({ id, mediaBlob, thumbnailBlob, data, url }) => {
        const allVideos = await db.externalVideos.toArray()
        const maxOrder = allVideos.length > 0 ? Math.max(...allVideos.map((v) => v.order)) : -1

        await db.externalVideos.add({
          id,
          title: data.title,
          videoBlob: mediaBlob,
          thumbnailBlob,
          originalUrl: url,
          savedAt: Date.now(),
          fileSize: mediaBlob.size,
          order: maxOrder + 1,
        })
      },
    }),
    [],
  )

  const { downloadState, startDownload, cancelDownload } = useExternalDownload(config)

  const downloadVideo = useCallback(
    (url: string, quality: 'high' | 'medium' | 'low' = 'high') => {
      startDownload(url, (onMessage, onError, signal) => {
        void streamExternalVideoDownload(url, quality, onMessage, onError, signal)
      })
    },
    [startDownload],
  )

  return { downloadState, downloadVideo, cancelDownload }
}
